import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronDown } from "react-icons/io5";
import { InlineSpinner } from "./SourceMessageAccordion";
import { timestampFormatter } from "../../../core/utils/timestampFormatter";
import {
  buildActionItemsListPath,
  formatEpochMsAsMeetingLabel,
  repairMojibakeText,
} from "../actionItemHelpers";

/**
 * Omit sync runs where nothing was scanned and no action items were created.
 *
 * @example
 * // Input: [{ sources_in_digest: 0, action_items_in_digest: 0 }, { sources_in_digest: 3, action_items_in_digest: 3 }]
 * // Output: [{ sources_in_digest: 3, action_items_in_digest: 3 }]
 */
export const getVisibleSyncHistory = (history) =>
  (history ?? []).filter(
    (entry) =>
      (Number(entry?.sources_in_digest) || 0) > 0 ||
      (Number(entry?.action_items_in_digest) || 0) > 0
  );

/**
 * Format a fetched-source occurrence timestamp for the accordion body.
 * Same calendar day as the sync run → time only; otherwise short datetime.
 *
 * @param {number} occurredAtMs - Occurrence epoch ms.
 * @param {number} [syncCompletedAtMs=0] - Sync run completed_at_ms for same-day check.
 * @returns {string}
 *
 * @example
 * // Input: occurredAtMs for 11:00 AM same day as sync
 * // Output: "11:00 AM"
 */
export const formatFetchedOccurrenceLabel = (
  occurredAtMs,
  syncCompletedAtMs = 0
) => {
  const occurred = Number(occurredAtMs) || 0;
  if (occurred <= 0) {
    return "";
  }
  const occurredDate = new Date(occurred);
  if (Number.isNaN(occurredDate.getTime())) {
    return "";
  }

  const syncMs = Number(syncCompletedAtMs) || 0;
  const sameDay =
    syncMs > 0 &&
    (() => {
      const syncDate = new Date(syncMs);
      return (
        !Number.isNaN(syncDate.getTime()) &&
        occurredDate.getFullYear() === syncDate.getFullYear() &&
        occurredDate.getMonth() === syncDate.getMonth() &&
        occurredDate.getDate() === syncDate.getDate()
      );
    })();

  if (sameDay) {
    let hours = occurredDate.getHours();
    const minutes = occurredDate.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes} ${ampm}`;
  }

  return timestampFormatter(occurred, { includeSeconds: false });
};

/**
 * Format a sync-run LLM cost for Activity UI (matches backend format_usd).
 *
 * @param {number|null|undefined} costUsd - Total cost in USD.
 * @returns {string} e.g. "$0.095", or empty when missing.
 *
 * @example
 * // Input: 0.09493
 * // Output: "$0.095"
 */
export const formatSyncRunCostUsd = (costUsd) => {
  if (costUsd === null || costUsd === undefined || costUsd === "") {
    return "";
  }
  const amount = Number(costUsd);
  if (!Number.isFinite(amount)) {
    return "";
  }
  return `$${amount.toFixed(3)}`;
};

/**
 * Pair each sync-run title with the fetched-occurrence timestamp (and
 * whether that specific source produced an action item) at the same index,
 * so a title can drive the same "Show all from this meeting" filter used
 * elsewhere in the action items list. Falls back to a time-only label when
 * there are more fetched occurrences than titles.
 *
 * @example
 * buildSyncRunSources({
 *   titles: ["Board prep"],
 *   fetchedOccurredAtMs: [1700000000000],
 *   hasActionItems: [true],
 * });
 * // [{ label: "Board prep", occurredAtMs: 1700000000000, hasActionItems: true }]
 */
export const buildSyncRunSources = (titles, fetchedOccurredAtMs, hasActionItems = []) => {
  const count = Math.max(titles.length, fetchedOccurredAtMs.length);
  const sources = [];
  for (let index = 0; index < count; index += 1) {
    const title = titles[index];
    const occurredAtMs = fetchedOccurredAtMs[index];
    if (!title && !occurredAtMs) continue;
    sources.push({
      label: repairMojibakeText(title || ""),
      occurredAtMs: occurredAtMs || 0,
      hasActionItems: Boolean(hasActionItems[index]),
    });
  }
  return sources;
};

/**
 * One source row inside a sync-run accordion body. Long titles are truncated
 * and expand in place on click; filtering to the meeting is a separate,
 * explicit button rather than the title text itself being the link.
 *
 * @param {object} props
 * @param {{label: string, occurredAtMs: number, hasActionItems: boolean}} props.source
 * @param {number} props.completedAtMs - Owning sync run's completed_at_ms.
 * @param {(occurredAtMs: number) => void} props.onFilterClick
 * @param {string} props.fallbackLabel - Label to use when the source has no title.
 */
const SyncRunSourceItem = ({
  source,
  completedAtMs,
  onFilterClick,
  fallbackLabel,
}) => {
  const [expanded, setExpanded] = useState(false);
  const timeLabel = formatFetchedOccurrenceLabel(source.occurredAtMs, completedAtMs);
  const displayLabel = source.label || timeLabel || fallbackLabel;
  const canExpand = Boolean(source.label);
  const canFilter = Boolean(source.occurredAtMs) && source.hasActionItems;

  const toggleExpanded = () => {
    if (canExpand) setExpanded((value) => !value);
  };

  return (
    <li className="ds-sync-history__fetched-item">
      <div className="ds-sync-history__fetched-item-row">
        <span
          className={`ds-sync-history__source-title${
            expanded ? " ds-sync-history__source-title--expanded" : ""
          }${canExpand ? " ds-sync-history__source-title--expandable" : ""}`}
          onClick={toggleExpanded}
          onKeyDown={(event) => {
            if (canExpand && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              toggleExpanded();
            }
          }}
          role={canExpand ? "button" : undefined}
          tabIndex={canExpand ? 0 : undefined}
          title={canExpand ? source.label : undefined}
        >
          {displayLabel}
        </span>
        {source.label && timeLabel ? (
          <span className="ds-sync-history__fetched-time"> · {timeLabel}</span>
        ) : null}
        {canFilter ? (
          <button
            type="button"
            className="ds-sync-history__filter-btn"
            onClick={() => onFilterClick(source.occurredAtMs)}
          >
            Filter by this meeting
          </button>
        ) : null}
      </div>
    </li>
  );
};

/**
 * One sync-history run as an accordion row (date + summary header, details when expanded).
 *
 * @param {object} props
 * @param {object} props.entry - Sync history entry.
 *
 * @example
 * <SyncHistoryAccordionRow
 *   entry={{
 *     completed_at_ms: 1,
 *     sources_in_digest: 2,
 *     action_items_in_digest: 1,
 *     total_cost_usd: 0.095,
 *     fetched_occurred_at_ms: [1100, 1130],
 *     has_action_items: [true, false],
 *     titles: ["Board prep with Acme"],
 *   }}
 * />
 */
const SyncHistoryAccordionRow = ({ entry, itemLabel = "meeting" }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const completedAtMs = Number(entry?.completed_at_ms) || 0;
  const actionItems = Number(entry?.action_items_in_digest) || 0;
  const sourcesInDigest = Number(entry?.sources_in_digest) || 0;
  const costLabel = formatSyncRunCostUsd(entry?.total_cost_usd);
  const fetchedOccurredAtMs = Array.isArray(entry?.fetched_occurred_at_ms)
    ? entry.fetched_occurred_at_ms
    : [];
  const titles = Array.isArray(entry?.titles) ? entry.titles : [];
  const hasActionItemsBySource = Array.isArray(entry?.has_action_items)
    ? entry.has_action_items
    : [];
  const sources = buildSyncRunSources(titles, fetchedOccurredAtMs, hasActionItemsBySource);
  const hiddenSourceCount = Math.max(0, sourcesInDigest - sources.length);
  const dateLabel =
    completedAtMs > 0
      ? timestampFormatter(completedAtMs, { includeSeconds: false })
      : "Latest run";
  const itemLabelPlural = `${itemLabel}s`;
  // A run that scanned sources but made nothing says so plainly: the sources may have
  // been processed earlier (e.g. before the connector was removed and re-added) or have
  // no tasks, which "0 action items created" alone doesn't tell apart.
  const scannedButNoneCreated = actionItems === 0 && sourcesInDigest > 0;
  const headerSummary = scannedButNoneCreated
    ? `No new action items from ${sourcesInDigest} ${sourcesInDigest === 1 ? itemLabel : itemLabelPlural}`
    : sourcesInDigest > 1
      ? `${actionItems} action items created across ${sourcesInDigest} ${itemLabelPlural}`
      : `${actionItems} action items created`;

  // Meeting-only filter (no workflow/source restriction) so items the same
  // meeting produced via a different connected app are never hidden.
  const handleSourceClick = (occurredAtMs) => {
    if (!occurredAtMs) return;
    const meetingLabel = formatEpochMsAsMeetingLabel(occurredAtMs);
    if (!meetingLabel) return;
    navigate(buildActionItemsListPath({ meeting: meetingLabel }));
  };

  return (
    <div
      className={`ds-sync-history__row${open ? " ds-sync-history__row--open" : ""}`}
    >
      <button
        type="button"
        className="ds-sync-history__row-toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="ds-sync-history__row-main">
          <span className="ds-sync-history__date">{dateLabel}</span>
          <span className="ds-sync-history__summary">{headerSummary}</span>
        </span>
        <IoChevronDown
          className={`ds-sync-history__row-chevron${open ? " ds-sync-history__row-chevron--open" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <div className="ds-sync-history__row-body">
          {scannedButNoneCreated ? (
            <p className="ds-sync-history__no-new-note">
              {itemLabelPlural.charAt(0).toUpperCase()}
              {itemLabelPlural.slice(1)} already processed earlier aren&apos;t read again, and some
              have no tasks.
            </p>
          ) : null}
          {sources.length > 0 || costLabel ? (
            <div className="ds-sync-history__fetched">
              <span className="ds-sync-history__fetched-label">
                {sources.length > 0
                  ? `${itemLabelPlural.charAt(0).toUpperCase()}${itemLabelPlural.slice(1)} synced`
                  : null}
                {costLabel ? (
                  <span className="ds-sync-history__fetched-cost">
                    {sources.length > 0 ? " · " : ""}Cost: {costLabel}
                  </span>
                ) : null}
              </span>
              {sources.length > 0 ? (
                <ul className="ds-sync-history__fetched-list">
                  {sources.map((source, index) => (
                    <SyncRunSourceItem
                      key={`${source.occurredAtMs}-${index}`}
                      source={source}
                      completedAtMs={completedAtMs}
                      onFilterClick={handleSourceClick}
                      fallbackLabel={`${itemLabel.charAt(0).toUpperCase()}${itemLabel.slice(1)} ${index + 1}`}
                    />
                  ))}
                  {hiddenSourceCount > 0 ? (
                    <li className="ds-sync-history__fetched-item ds-sync-history__fetched-item--more">
                      + {hiddenSourceCount} more {itemLabel}
                      {hiddenSourceCount === 1 ? "" : "s"} synced in this run
                    </li>
                  ) : null}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Activity totals and per-run sync history for connector settings (web + extension).
 * Each Sync history entry is its own accordion row.
 *
 * @param {object} props
 * @param {object|null} props.syncStats - Cumulative + history payload.
 * @param {boolean} [props.loading=false] - Show loading spinner.
 * @param {string} [props.className=""] - Extra root class.
 * @param {string} [props.workflowType=""] - Frontend workflow_type id (e.g. "slack_daily_digest")
 *   used to pick "message"/"meeting" wording in sync history copy.
 *
 * @example
 * <WorkflowSyncActivityPanel
 *   syncStats={{ cumulative: { total_sources_extracted: 8, total_action_items_created: 31, total_cost_usd_this_month: 0.287 }, history: [...] }}
 *   loading={false}
 * />
 */
const WorkflowSyncActivityPanel = ({
  syncStats,
  loading = false,
  className = "",
  workflowType = "",
}) => {
  const itemLabel = workflowType === "slack_daily_digest" ? "message" : "meeting";
  const visibleSyncHistory = getVisibleSyncHistory(syncStats?.history);
  const rootClassName = ["ds-detail__sync-stats", className].filter(Boolean).join(" ");
  const monthlyCostLabel = formatSyncRunCostUsd(
    syncStats?.cumulative?.total_cost_usd_this_month ?? 0
  );

  return (
    <div className={rootClassName}>
      <h4 className="ds-sync-history__title">Activity</h4>
      {loading ? (
        <InlineSpinner text="Loading activity…" />
      ) : (
        <>
          <div className="ds-sync-summary">
            <div className="ds-sync-summary__item">
              <strong>{syncStats?.cumulative?.total_sources_extracted ?? 0}</strong>
              <span>scanned</span>
            </div>
            <div className="ds-sync-summary__item">
              <strong>{syncStats?.cumulative?.total_action_items_created ?? 0}</strong>
              <span>action items created</span>
            </div>
            <div className="ds-sync-summary__item">
              <strong>{monthlyCostLabel || "$0.000"}</strong>
              <span>this month</span>
            </div>
          </div>
          {visibleSyncHistory.length ? (
            <div className="ds-sync-history">
              <h4 className="ds-sync-history__title">Sync history</h4>
              <div className="ds-sync-history__list">
                {visibleSyncHistory.map((entry, index) => (
                  <SyncHistoryAccordionRow
                    key={entry?.job_uuid || index}
                    entry={entry}
                    itemLabel={itemLabel}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default WorkflowSyncActivityPanel;
